/**
 * @fileoverview Individual ranking table of the Sales Performance screen.
 * @description Lists the people of the Sales Performance ranking with their position, name, role,
 * assigned and achieved targets and an achievement bar, in the order the backend ranked them. The
 * backend caps the list, so when more people matched than are shown the card says so. A caller who
 * only sees themselves gets a one-row table titled as their own result.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { achievementBarClass, formatPercent } from '@/features/sales/lib/sales-performance.utils'

import TableEmptyState from './TableEmptyState'

import type { SalesPerformanceRanking } from '@/features/sales/types/sales-performance.types'

const GRID = 'grid-cols-[40px_minmax(150px,2fr)_minmax(110px,1.2fr)_80px_80px_minmax(140px,1.5fr)]'
const COLUMN_KEYS = ['rank', 'name', 'role', 'assigned', 'achieved', 'achievement'] as const

interface PerformanceRankingTableProps {
  ranking: SalesPerformanceRanking
}

/**
 * @component
 * @param {PerformanceRankingTableProps} props - The ranking to show.
 * @param {SalesPerformanceRanking} props.ranking - The ranked people and the total before the cap.
 */
function PerformanceRankingTable({ ranking }: PerformanceRankingTableProps) {
  const { t } = useTranslation()

  if (ranking.items.length === 0) {
    return (
      <TableEmptyState
        message={t('sales.salesPerformance.ranking.emptyTitle')}
        description={t('sales.salesPerformance.ranking.emptyDescription')}
        icon="trend"
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[640px]">
        <div
          className={`grid ${GRID} items-center gap-3 border-y border-divider bg-input-bg px-3 py-2.5`}
        >
          {COLUMN_KEYS.map((key) => (
            <span
              key={key}
              className="text-caption font-semibold tracking-caption text-table-header"
            >
              {t(`sales.salesPerformance.ranking.columns.${key}`)}
            </span>
          ))}
        </div>
        {ranking.items.map((person) => (
          <div
            key={person.userId}
            className={`grid ${GRID} items-center gap-3 border-b border-row-border px-3 py-2.5`}
            data-testid="sales-performance-ranking-row"
          >
            <span
              className="font-bold text-slate tabular-nums"
              data-testid="sales-performance-ranking-rank"
            >
              {person.rank}
            </span>
            <span
              className="truncate text-compact-lg font-semibold text-ink"
              data-testid="sales-performance-ranking-name"
            >
              {person.name}
            </span>
            <span className="text-compact text-cell" data-testid="sales-performance-ranking-role">
              {t(`sales.salesPerformance.roles.${person.role}`)}
            </span>
            <span
              className="text-compact tabular-nums"
              data-testid="sales-performance-ranking-assigned"
            >
              {person.assigned}
            </span>
            <span
              className="text-compact tabular-nums"
              data-testid="sales-performance-ranking-achieved"
            >
              {person.achieved}
            </span>
            <div className="flex items-center gap-2.5">
              <div className="h-2 flex-1 overflow-hidden rounded bg-input-bg">
                <div
                  className={`h-full ${achievementBarClass(person.achievementPercent)}`}
                  style={{ width: `${String(person.achievementPercent ?? 0)}%` }}
                />
              </div>
              <b
                className="w-10 text-right text-compact-sm tabular-nums"
                data-testid="sales-performance-ranking-achievement"
              >
                {formatPercent(person.achievementPercent)}
              </b>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PerformanceRankingTable
